import get from "lodash/get";
import isString from "lodash/isString";
import sortBy from "lodash/sortBy";

import { isPresent } from "~/tools/is-present";
import { isTruthy } from "~/tools/is-truthy";
import { max } from "~/tools/max";
import { min } from "~/tools/min";
import { pow } from "~/tools/pow";

import type { SelectOption, SelectOptionGroup } from "./types";

const UNMATCHED_CHAR_SCORE = 100;
const QUERY_GAP_SCORE = 10;
const UNMATCHED_CHAR_CASE_SCORE = 1;

/**
 * Lower score means better match.
 * The lowest score is 1.
 * If there is no match, false is returned.
 */
const fuzzScore = <TValue>(query: string, paths: Array<string>, value: TValue): false | number => {
	const queryLower = query.toLowerCase();

	const scores = paths
		.map((path) => get(value, path, "") as unknown)
		.filter((field): field is string => isString(field) && field.length >= query.length)
		.map((field) => {
			const fieldLower = field.toLowerCase();

			let queryIndex = 0;
			let fieldIndex = 0;
			let score = 1;

			for (let gap = 0; queryIndex < query.length && fieldIndex < field.length; fieldIndex++) {
				if (queryLower.at(queryIndex) === fieldLower.at(fieldIndex)) {
					// if the lowered characters match, but the original characters do not, add a small penalty
					if (query.at(queryIndex) !== field.at(fieldIndex)) {
						score += UNMATCHED_CHAR_CASE_SCORE;
					}

					// the gap score in power of 10, so the longer the gap, the worse is the match
					score += QUERY_GAP_SCORE * pow(10, gap);
					queryIndex++;
					gap = 0;
				} else {
					// unmatched characters are penalized
					score += UNMATCHED_CHAR_SCORE;
					// gaps within the query are penalized
					gap++;
				}
			}

			// if the query is not fully matched, the score is false
			if (queryIndex < query.length) {
				return false;
			}

			// increase the scope proportionally to the length of the unmatched field
			return score + UNMATCHED_CHAR_SCORE * (field.length - fieldIndex);
		});

	return min(scores.filter(isTruthy)) ?? false;
};

interface FuzzItem<TValue> {
	score: false | number;
	value: TValue;
}

const filterScored = <TValue>(items: Array<FuzzItem<TValue>>): Array<TValue> =>
	items.filter(({ score }) => score).map(({ value }) => value);

/**
 * The fuzz search performs a fuzzy search on the given values and attaches the score to each value.
 * The minScore is the lowest score of all values if any. Otherwise false.
 *
 * The FuzzySearch#search() method is not used because it does not attach the score.
 */
const fuzzSearch = <TValue>(
	query: string,
	paths: Array<string>,
	values: Array<TValue>,
): {
	minScore: false | number;
	results: Array<FuzzItem<TValue>>;
} => {
	const results = values.map((value) => ({
		score: fuzzScore(query, paths, value),
		value,
	}));

	const scores = results.map(({ score }) => score).filter(isTruthy);
	const minScore = min(scores);
	const maxScore = max(scores);

	if (minScore == null || maxScore == null) {
		return { minScore: false, results };
	}

	return {
		minScore,
		results: sortBy(results, ({ score }) => score || maxScore + 1),
	};
};

export const fuzzSearchInOptions = <TValue>(
	query: string,
	paths: Array<string>,
	options: Array<SelectOption<TValue>>,
): Array<SelectOption<TValue>> => {
	if (query.length === 0) {
		return options;
	}

	const { results } = fuzzSearch(query, paths, options);

	return filterScored(results);
};

export const fuzzSearchInGroups = <TValue>(
	query: string,
	paths: Array<string>,
	groups: Array<SelectOptionGroup<TValue>>,
): Array<SelectOptionGroup<TValue>> => {
	if (query.length === 0) {
		return groups;
	}

	const results = groups.map((group) => {
		const groupScore = fuzzScore(query, ["label"], group);
		const options = fuzzSearch(query, paths, group.options);

		// if the group label is matched, it shows all of its options sorted by their score
		if (groupScore !== false) {
			return {
				score: options.minScore === false ? groupScore : min(groupScore, options.minScore),
				group: {
					...group,
					// keep all options even if they are not matched
					options: options.results.map(({ value }) => value),
				},
			};
		}

		// if the group label is not matched nor any of its options, it is not shown
		if (options.minScore === false) {
			return null;
		}

		// otherwise it shows only the matched options sorted by their score
		return {
			score: options.minScore,
			group: {
				...group,
				options: filterScored(options.results),
			},
		};
	});

	return results
		.filter(isPresent)
		.sort((left, right) => left.score - right.score)
		.map(({ group }) => group);
};
